import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Download, FileText, X, CheckCircle } from 'lucide-react';

const ResourcesPage = () => {
  const [selectedTab, setSelectedTab] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    interest: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadLinks, setDownloadLinks] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const resources = [
    {
      id: 'ats-resume-template',
      title: 'ATS-Ready IT Resume Template 2025',
      description: 'AI & IT job-optimized layout that passes applicant tracking systems',
      category: 'Jobs',
      icon: '💼',
      formats: ['PDF', 'DOCX']
    },
    {
      id: 'ai-interview-questions',
      title: 'Top 100 AI Interview Questions & Answers',
      description: 'Technical + behavioral questions with detailed answers',
      category: 'AI & Cloud',
      icon: '🧠',
      formats: ['PDF']
    },
    {
      id: 'interview-prep-checklist',
      title: 'CareerLift Interview Prep Checklist',
      description: 'Ready-to-use printable checklist for interview success',
      category: 'Jobs',
      icon: '✅',
      formats: ['PDF']
    },
    {
      id: 'linkedin-optimization',
      title: 'LinkedIn Optimization Guide for Tech Roles',
      description: 'Keyword and visibility tips to get noticed by recruiters',
      category: 'Jobs',
      icon: '🔗',
      formats: ['PDF']
    },
    {
      id: 'ai-career-roadmap',
      title: 'AI Career Roadmap 2025',
      description: 'Pathways for AI Engineer, Architect, and Prompt Engineer roles',
      category: 'AI & Cloud',
      icon: '🗺️',
      formats: ['PDF']
    },
    {
      id: 'portfolio-template',
      title: 'Portfolio Project Template',
      description: 'Document AI/Cloud/DevOps projects professionally',
      category: 'Career Planning',
      icon: '📁',
      formats: ['PDF', 'DOCX']
    },
    {
      id: 'ai-tool-stack',
      title: 'AI Tool Stack Cheat Sheet 2025',
      description: 'Categorized tools for productivity and learning',
      category: 'AI & Cloud',
      icon: '🛠️',
      formats: ['PDF']
    },
    {
      id: 'skill-tracker',
      title: 'CareerLift Skill Tracker (Editable)',
      description: 'Track certifications, progress, and learning hours',
      category: 'Career Planning',
      icon: '📊',
      formats: ['PDF', 'XLSX']
    }
  ];

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'Jobs', label: 'Jobs' },
    { id: 'AI & Cloud', label: 'AI & Cloud' },
    { id: 'Career Planning', label: 'Career Planning' }
  ];

  const filteredResources = selectedTab === 'all' 
    ? resources 
    : resources.filter(r => r.category === selectedTab);

  const handleDownloadClick = (resource) => {
    setSelectedResource(resource);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consent) {
      alert('Please accept the Privacy Policy to continue');
      return;
    }

    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/download-resource`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          first_name: formData.first_name,
          interest: formData.interest,
          resource_title: selectedResource.title,
          resource_id: selectedResource.id,
          consent: formData.consent,
          page_url: window.location.href,
          utm_source: new URLSearchParams(window.location.search).get('utm_source') || '',
          utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || '',
          utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || ''
        })
      });

      if (!response.ok) {
        throw new Error('Failed to process request');
      }

      const result = await response.json();
      setDownloadLinks(result.download_links);
      setShowSuccess(true);

      // Set cookie
      document.cookie = 'careerlift_downloaded=true; max-age=2592000; path=/';

    } catch (error) {
      console.error('Download error:', error);
      alert('There was an issue processing your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setShowSuccess(false);
    setDownloadLinks(null);
    setFormData({
      email: '',
      first_name: '',
      interest: '',
      consent: false
    });
  };

  return (
    <div className="min-h-screen bg-[#F9F9FB]">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-white to-[#F8F6FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#0E0E16] mb-6">
            Free Career Resources
          </h1>
          <p className="text-xl text-[#4A4A55] max-w-3xl mx-auto">
            Templates, guides, and tools to accelerate your IT career
          </p>
        </div>
      </section>

      {/* Tabs */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id)}
                className={`px-6 py-2 rounded-full font-semibold text-base transition-all duration-300 ${
                  selectedTab === tab.id
                    ? 'bg-[#4B3FFF] text-white'
                    : 'bg-gray-100 text-[#4A4A55] hover:bg-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredResources.map((resource) => (
              <div
                key={resource.id}
                className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-[#4B3FFF] hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="text-5xl mb-4">{resource.icon}</div>
                
                <span className="inline-block px-3 py-1 bg-[#F8F6FF] text-[#4B3FFF] text-sm font-semibold rounded-full mb-3">
                  {resource.category}
                </span>
                
                <h3 className="text-xl font-bold text-[#0E0E16] mb-2">
                  {resource.title}
                </h3>
                
                <p className="text-[#4A4A55] mb-4 line-height-1.5">
                  {resource.description}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="text-sm text-[#767680]">
                    {resource.formats.join(' • ')}
                  </div>
                  
                  <button
                    onClick={() => handleDownloadClick(resource)}
                    className="px-5 py-2.5 bg-[#4B3FFF] hover:bg-[#3B2EDC] text-white font-semibold rounded-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(75,63,255,0.3)] inline-flex items-center gap-2"
                  >
                    <Download size={18} />
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 relative animate-fadeIn">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>

            {!showSuccess ? (
              <>
                <div className="text-center mb-6">
                  <div className="text-5xl mb-3">{selectedResource?.icon}</div>
                  <h2 className="text-2xl font-bold text-[#0E0E16] mb-2">
                    Get Your Free Resource
                  </h2>
                  <p className="text-[#4A4A55]">{selectedResource?.title}</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#0E0E16] mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#4B3FFF] focus:outline-none"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#0E0E16] mb-2">
                      First Name (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#4B3FFF] focus:outline-none"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#0E0E16] mb-2">
                      I'm Interested In
                    </label>
                    <select
                      value={formData.interest}
                      onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-[#4B3FFF] focus:outline-none"
                    >
                      <option value="">Select an option</option>
                      <option value="AI Career Launchpad">AI Career Launchpad</option>
                      <option value="AI Engineer">AI Engineer</option>
                      <option value="Cloud Engineer">Cloud Engineer</option>
                      <option value="DevOps Engineer">DevOps Engineer</option>
                      <option value="Cybersecurity Specialist">Cybersecurity Specialist</option>
                      <option value="Bootcamps">Bootcamps</option>
                    </select>
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="consent"
                      checked={formData.consent}
                      onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                      className="mt-1"
                      required
                    />
                    <label htmlFor="consent" className="text-sm text-[#4A4A55]">
                      I agree to be contacted and accept the{' '}
                      <a href="/privacy" className="text-[#4B3FFF] hover:underline">
                        Privacy Policy
                      </a>
                      .
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-7 py-3.5 bg-[#4B3FFF] hover:bg-[#3B2EDC] text-white font-semibold text-base rounded-xl transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(75,63,255,0.3)] disabled:opacity-50"
                  >
                    {isSubmitting ? 'Processing...' : 'Get Download'}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-[#35B96E] mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-[#0E0E16] mb-3">
                  Request Received!
                </h2>
                <p className="text-[#4A4A55] mb-4">
                  Thank you! We'll send <strong>{selectedResource?.title}</strong> to <strong>{formData.email}</strong> within 24 hours.
                </p>
                
                <div className="bg-[#F8F6FF] p-4 rounded-lg mb-6">
                  <p className="text-sm text-[#4A4A55]">
                    📧 Check your inbox (and spam folder) for an email from <strong>info@careerlift.com.au</strong>
                  </p>
                </div>

                <button
                  onClick={closeModal}
                  className="px-7 py-3 bg-[#4B3FFF] hover:bg-[#3B2EDC] text-white font-semibold rounded-lg transition-all"
                >
                  Continue Browsing Resources
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ResourcesPage;
